import React, { useCallback, useEffect, useState } from "react";
import { SkeletonRows } from "../../../components/ui";
import { useSession } from "../../../context/SessionContext";
import { useApi } from "../../../hooks/useApi";
import { plural, t } from "../../../i18n";
import { ptBR } from "../../../i18n/pt-BR";
import { showError } from "../../../lib/feedback";
import { packagesService } from "../../../services/packages";
import { KitBuilder } from "../../kits";
import { AddLink, ITEM_CLASS } from "./LinkedList";

interface Row {
  key: string;
  name: string;
  subtitle: string;
}

// Pacotes personalizados do médico (procedimentos e OPME) e o "+ Novo pacote".
export const CustomKits: React.FC = () => {
  const { metadata } = useSession();
  const { apiFetch } = useApi();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    // Sem histórico e laudo: aqui não há pedido para a IA comparar.
    const body = { email: metadata.email, historico: "", laudo: "", recomendar: false };
    try {
      const [procs, opmes] = await Promise.all([
        packagesService.procedureKits(apiFetch, "personalizados", body),
        packagesService.opmeKits(apiFetch, "personalizados", body),
      ]);
      setRows([
        ...procs.map((k) => ({ key: `p${k.id}`, name: k.nome, subtitle: t("settings.kits.procedures", { items: plural(k.procedimentos.length, ptBR.kitBuilder.items) }) })),
        ...opmes.map((k) => ({ key: `o${k.id}`, name: k.nome, subtitle: t("settings.kits.opme", { items: plural(k.opmes.length, ptBR.kitBuilder.items) }) })),
      ]);
    } catch (err) {
      showError(err);
      setRows([]);
    }
  }, [apiFetch, metadata.email]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="flex flex-col gap-3">
      {rows === null ? (
        <SkeletonRows label={t("settings.kits.loading")} />
      ) : rows.length === 0 ? (
        <p className="py-1 text-caption text-fg-subtle">{t("settings.kits.empty")}</p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {rows.map((r) => (
            <li key={r.key} className={ITEM_CLASS}>
              <span className="min-w-0 flex-1">
                <span className="block break-words text-body text-fg">{r.name}</span>
                <span className="block text-caption text-fg-subtle">{r.subtitle}</span>
              </span>
            </li>
          ))}
        </ul>
      )}

      <AddLink onClick={() => setCreating(true)}>{t("settings.kits.add")}</AddLink>

      {creating && (
        <KitBuilder
          kind="proc"
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
};
